import React, { useMemo, useState } from 'react';
import styles from './WorkplaceWorkerInsuranceSettings.module.scss';
import { MetaKey } from '../../../../Meta/Enums/MetaKey.enum';
import { useAppContext } from '../../../../../Services/AppContext';
import useUserMeta from '../../../../User/Hooks/useUserMeta';
import {
	Meta,
	TermItem,
	useLazyMetaQuery,
	useLazyTermItemsQuery,
} from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import NCZISetupInsuranceRow from '../../../../Nczi/Components/NCZISetup/NCZISetupInsuranceRow/NCZISetupInsuranceRow';
import useRootWorkplace from '../../../Hooks/useRootWorkplace';
import { WorkplaceInsuranceSettings } from '../../../../Nczi/Interfaces/insuranceSettings';
import { NcziSetupSettings } from '../../../../Nczi/Components/NCZISetup/NcziSetupSettings';
import apiClient from '../../../../../Services/ApiClient';
import useNotificationHook from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';
import { NCZISetupInsuranceCompanyValues } from '../../../../Nczi/Components/NCZISetup/NCZISetupInsuranceCompany/NCZISetupInsuranceCompany';
import Text from '../../../../../Components/Base/Text/Text';

interface Props {
	userId: string;
}

const WorkplaceWorkerInsuranceSettings: React.FC<Props> = ({ userId }) => {
	const { userMeta, reFetchUserMeta } = useUserMeta({ userId });
	const [getTermItems] = useLazyTermItemsQuery();
	const [insuranceCompanies, setInsuranceCompanies] = useState<TermItem[]>([]);
	const [getMeta] = useLazyMetaQuery();
	const { workplaceId } = useAppContext();
	const { rootWorkplaceId } = useRootWorkplace(workplaceId, userId);
	const [workplaceInsurance, setWorkplaceInsurance] =
		useState<WorkplaceInsuranceSettings>({});
	const { showNotificationSuccess } = useNotificationHook();
	const { t } = useTranslation();

	const userSetup: Meta | undefined = useMemo(() => {
		return userMeta[MetaKey.NCZI] ?? undefined;
	}, [userMeta]);

	const saveSettings = async (settings: NcziSetupSettings) => {
		await apiClient.post(`/user/${userId}/meta`, {
			key: 'nczi',
			value: settings,
		});

		showNotificationSuccess({ content: t('component.nczi.savedSuccessfully') });
	};

	const handleSaveInsurance = async (
		insuranceCompanyKey: string,
		values: NCZISetupInsuranceCompanyValues,
	) => {
		const tmpInsuranceSettings = { ...userSetup!.insuranceSettings };

		for (const insuranceCompany of insuranceCompanies) {
			const insuranceKey = insuranceCompany.value3 || 'default';
			if (typeof tmpInsuranceSettings[insuranceKey] === 'undefined') {
				tmpInsuranceSettings[insuranceKey] = null;
			}
		}

		tmpInsuranceSettings[insuranceCompanyKey] = values;
		const newSettings = {
			...userSetup!,
			insuranceSettings: tmpInsuranceSettings,
		} as NcziSetupSettings;
		await saveSettings(newSettings);
		await reFetchUserMeta();
	};

	useEffectAsync(async () => {
		const { termItems } = await getTermItems({
			termType: 'insurance_companies',
		}).unwrap();

		if (termItems?.data || []) {
			const tmpTermItems = [];
			for (const termItem of termItems?.data || []) {
				if (termItem) {
					tmpTermItems.push(termItem);
				}
			}
			setInsuranceCompanies(tmpTermItems);
		}

		const { meta: workplaceMeta } = await getMeta({
			key: 'nczi',
			workplaceId: rootWorkplaceId,
		}).unwrap();

		if (workplaceMeta?.value) {
			setWorkplaceInsurance(workplaceMeta.value.insurance || {});
		}
	});

	return (
		<div className={styles.wrapper}>
			<div className={styles.title}>
				<Text tx={'component.nczi.setup.insuranceSetup'} />
			</div>
			{!userSetup && <Text tx={'component.nczi.setup.missingSettings'} />}
			{userSetup && (
				<>
					{(insuranceCompanies ?? []).map((insuranceCompany, i) => (
						<div className={styles.item} key={i}>
							<NCZISetupInsuranceRow
								insuranceCompany={insuranceCompany}
								userSettings={userSetup}
								workplaceInsurance={workplaceInsurance}
								saveInsuranceSettings={values =>
									handleSaveInsurance(
										insuranceCompany.value3 || 'default',
										values,
									)
								}
							/>
						</div>
					))}
				</>
			)}
		</div>
	);
};

export default WorkplaceWorkerInsuranceSettings;
